"use client";

import { getUserAlbums, postAlbum } from "@/api/album";
import { updatePostCollect } from "@/api/posts";
import Iconify from "@/components/ui/Iconify";
import { useAuthContext } from "@/context/auth/useAuthContext";
import { enqueueSnackbar } from "notistack";
import { useEffect, useRef, useState } from "react";

/**
 * 저장 → 앨범 담기 시트.
 *
 * 예전에는 저장하면 영상 위에 흰 띠가 떠서 "Successfully collected / Add to album >"
 * 라고 영어로 알렸고, 거기서 한 번 더 눌러야 앨범 목록이 나왔다. 문제가 넷이었다.
 *  1) 사라지는 알림(토스트)에 사라지면 안 되는 행동(담기)을 얹었다 — 놓치면 길이 없다.
 *  2) 그 띠가 장소 칩과 같은 층에서 겹쳤다.
 *  3) 흰 판이 영상 위에 떠 있어, 아래에서 올라오는 앱의 다른 시트와 문법이 달랐다.
 *  4) 영어였다.
 *
 * 그리고 애초에 나눌 일이 아니었다. 저장하면 Collect 한 줄이 생기고, 앨범에 담는 건
 * 그 줄에 이름표(Collect.albumId)를 붙이는 것뿐이다. 같은 것의 분류다.
 * 장소는 이미 저장을 누르면 곧바로 목록 고르기가 열린다 — 플레이만 달랐다.
 *
 * 그래서 하나로 합쳤다. 저장은 누르는 순간 이미 끝나 있고, 이 시트는 "어디에 담을지"만
 * 묻는다. 닫거나 "나중에"를 눌러도 저장은 남으므로 마찰이 늘지 않는다.
 * 앨범은 Collect 당 하나(albumId 가 단일 필드)라 고르기는 단일 선택이다.
 *
 * "새 앨범 만들기"를 목록 맨 위에 뒀다. 예전에는 앨범을 만들려면 프로필까지 가야 했다 —
 * 앨범이 비어 있던 이유가 이것일 가능성이 크다.
 */
const AlbumAlert = ({ setShowAlbemAlert, currentCollectId }) => {
  const { user } = useAuthContext();
  const [albums, setAlbums] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState("");
  const [saving, setSaving] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const newTitleRef = useRef(null);

  const close = () => setShowAlbemAlert(false);

  const fetchAlbums = async () => {
    if (!user?.userId) {
      setLoading(false);
      return;
    }
    try {
      const list = await getUserAlbums(user.userId);
      setAlbums(Array.isArray(list) ? list : []);
    } catch (error) {
      // 목록을 못 불러와도 시트는 닫지 않는다 — 저장 자체는 이미 끝났고
      // "새 앨범 만들기"는 여전히 쓸 수 있다.
      setAlbums([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAlbums();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.userId]);

  useEffect(() => {
    const onKey = (event) => {
      if (event.key === "Escape") close();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (creating) newTitleRef.current?.focus();
  }, [creating]);

  const handleCreate = async () => {
    const title = newTitle.trim();
    if (!title) return;
    if (title.length > 12) {
      enqueueSnackbar("앨범 이름은 12자까지 쓸 수 있어요", { variant: "warning" });
      return;
    }
    try {
      setSaving(true);
      const created = await postAlbum({ albumTitle: title, isPublic: false });
      if (created?.id) {
        setAlbums((prev) => [created, ...prev]);
        setSelectedId(created.id);
      }
      setCreating(false);
      setNewTitle("");
    } catch (error) {
      enqueueSnackbar(
        error?.response?.data?.message || error?.message || "앨범을 만들지 못했어요",
        { variant: "error" }
      );
    } finally {
      setSaving(false);
    }
  };

  const handleConfirm = async () => {
    if (!selectedId || !currentCollectId) {
      close();
      return;
    }
    try {
      setSaving(true);
      await updatePostCollect({ collectId: currentCollectId, albumId: selectedId });
      close();
    } catch (error) {
      enqueueSnackbar(
        error?.response?.data?.message || error?.message || "앨범에 담지 못했어요",
        { variant: "error" }
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    // 하단 네비(z-[95])보다 위여야 "나중에"가 가려지지 않는다.
    <div
      className="fixed inset-0 z-[120] flex items-end"
      role="dialog"
      aria-modal="true"
      aria-label="앨범에 담기"
      onClick={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        aria-label="닫기"
        onClick={close}
        className="absolute inset-0 bg-black/40"
      />
      <div className="relative max-h-[82vh] w-full overflow-y-auto rounded-t-[20px] bg-surface px-[18px] pt-[14px] pb-[calc(18px+env(safe-area-inset-bottom))] shadow-[0_-6px_24px_rgba(0,0,0,0.18)] dark:bg-surface-2dark">
        <span className="mx-auto mb-[14px] block h-[4px] w-[34px] rounded-full bg-ink/10 dark:bg-white/20" />

        <div className="flex items-center gap-[10px]">
          <span className="grid h-[38px] w-[38px] flex-none place-items-center rounded-chip bg-action/[0.09] text-action">
            <Iconify icon="material-symbols:bookmark" className="!h-[18px] !w-[18px]" />
          </span>
          <span className="min-w-0">
            <span className="block text-t-title">저장했어요</span>
            <span className="block text-t-meta text-ink-45 dark:text-ink_on_dark-45">
              앨범에 담아 두면 나중에 찾기 쉬워요
            </span>
          </span>
        </div>

        <div className="mt-3 border-t border-hairline dark:border-hairline-dark">
          {creating ? (
            <div className="flex items-center gap-2 py-3">
              <input
                ref={newTitleRef}
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleCreate()}
                maxLength={12}
                placeholder="앨범 이름 (12자까지)"
                className="min-w-0 flex-1 rounded-chip border border-hairline bg-surface px-3 py-2 text-t-body text-ink outline-none placeholder:text-ink-25 focus:border-action dark:border-hairline-dark dark:bg-white/[0.04] dark:text-ink_on_dark"
              />
              <button
                type="button"
                onClick={handleCreate}
                disabled={saving || !newTitle.trim()}
                className="shrink-0 rounded-chip bg-action px-3 py-2 text-t-label font-extrabold text-white disabled:opacity-40"
              >
                만들기
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setCreating(true)}
              className="flex w-full items-center gap-3 py-[15px] text-left"
            >
              <span className="grid h-[34px] w-[34px] flex-none place-items-center rounded-chip border-2 border-dashed border-ink-25 text-ink-45">
                <Iconify icon="material-symbols-light:add" className="!h-5 !w-5" />
              </span>
              <span className="text-t-item text-ink-70 dark:text-ink_on_dark-70">
                새 앨범 만들기
              </span>
            </button>
          )}
        </div>

        {loading ? (
          <div className="flex justify-center py-8">
            <Iconify
              icon="teenyicons:loader-outline"
              className="!h-5 !w-5 animate-spin text-ink-25"
            />
          </div>
        ) : (
          albums.map((album) => {
            const checked = selectedId === album?.id;
            return (
              <button
                key={album?.id}
                type="button"
                onClick={() => setSelectedId(checked ? "" : album?.id)}
                className="flex w-full items-center gap-3 border-t border-hairline py-[15px] text-left dark:border-hairline-dark"
              >
                <span className="h-[34px] w-[34px] flex-none rounded-chip bg-surface-2 dark:bg-white/[0.08]" />
                <span className="min-w-0 flex-1 truncate text-t-item">
                  {album?.albumTitle}
                </span>
                <span
                  className={
                    checked
                      ? "h-[22px] w-[22px] flex-none rounded-full border-2 border-action bg-action"
                      : "h-[22px] w-[22px] flex-none rounded-full border-2 border-ink-25"
                  }
                />
              </button>
            );
          })
        )}

        <div className="mt-4 flex gap-[10px]">
          <button
            type="button"
            onClick={close}
            className="h-[46px] flex-1 rounded-card border border-hairline text-t-item dark:border-hairline-dark"
          >
            나중에
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={saving || !selectedId}
            className="h-[46px] flex-[1.4] rounded-card bg-action text-t-item font-extrabold text-white disabled:opacity-40"
          >
            담기
          </button>
        </div>
      </div>
    </div>
  );
};

export default AlbumAlert;
